import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ladeEinladung,
  listeEinladungen,
  nimmEinladungAn,
  lehneEinladungAb,
} from '@/api/einladungen.api';
import { useTreeContextStore } from '@/store/useTreeContextStore';

// ─── Einladung annehmen oder ablehnen ────────────────────────────────────────
//
// Die Seite hinter dem Link aus der E-Mail. Vor der Entscheidung steht, worum
// es geht: welcher Stammbaum, wer einlädt, welche Rolle vorgesehen ist. Ein
// Knopf „Annehmen" ohne diese Angaben wäre eine Zustimmung ins Blaue.
//
// „Ablehnen" steht gleichberechtigt daneben und ist nicht kleiner gesetzt.
// Wer nicht möchte, soll nicht suchen müssen, wie er absagt.
//
// Ohne Token zeigt dieselbe Seite, was sonst noch offen ist. Das ist der Weg
// für den Fall, dass die E-Mail nie ankam – ein Spamfilter, ein fehlender
// Mailserver, ein Tippfehler in der Adresse.

function Datum({ wert }: { wert: string }) {
  return (
    <>
      {new Date(wert).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })}
    </>
  );
}

/**
 * Eigenständige Seite, nicht im AppLayout.
 *
 * Wer seiner ERSTEN Einladung folgt, hat noch keinen Stammbaum – und das
 * Layout der Anwendung setzt einen voraus. Er stünde vor „Kein Stammbaum
 * ausgewählt", statt vor der Frage, die er beantworten soll.
 */
function Rahmen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-xl">{children}</div>
    </div>
  );
}

// ─── Ohne Token: die Liste ───────────────────────────────────────────────────

function OffeneListe() {
  const { data, isLoading } = useQuery({
    queryKey: ['einladungen'],
    queryFn: listeEinladungen,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <>
        <h1 className="text-xl font-semibold text-slate-900">Einladungen</h1>
        <p className="mt-6 rounded-lg border border-dashed border-slate-200 px-4 py-12 text-center text-sm text-slate-400">
          Zurzeit wartet keine Einladung auf Ihre Antwort.
        </p>
      </>
    );
  }

  return (
    <>
      <h1 className="text-xl font-semibold text-slate-900">Einladungen</h1>
      <p className="mt-1 text-sm text-slate-500">
        Diese Stammbäume möchten Sie aufnehmen. Solange Sie nicht zustimmen,
        sehen Sie sie nicht.
      </p>
      <ul className="mt-6 space-y-3">
        {data.map((e) => (
          <li key={e.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p className="font-medium text-slate-900">{e.baumName}</p>
            <p className="mt-1 text-sm text-slate-500">
              {e.einladerEmail ? `Eingeladen von ${e.einladerEmail}` : 'Eingeladen'} · Rolle:{' '}
              {e.rolleName}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Gültig bis <Datum wert={e.gueltigBis} />
            </p>
            <p className="mt-3 text-xs text-slate-500">
              Die Entscheidung treffen Sie über den Link in der E-Mail, die an
              Ihre Adresse gegangen ist.
            </p>
          </li>
        ))}
      </ul>
    </>
  );
}

// ─── Mit Token: die Entscheidung ─────────────────────────────────────────────

export default function EinladungPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const gleichAblehnen = params.get('antwort') === 'ablehnen';

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setzeAktivenBaum = useTreeContextStore((s) => s.setActiveTree);
  const [erledigt, setErledigt] = useState<'angenommen' | 'abgelehnt' | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['einladung', token],
    queryFn: () => ladeEinladung(token),
    enabled: token !== '',
    retry: false,
  });

  const annehmen = useMutation({
    mutationFn: () => nimmEinladungAn(token),
    onSuccess: (ergebnis) => {
      setErledigt('angenommen');
      // Der neue Baum soll sofort erreichbar sein – sonst müsste man nach dem
      // Zusagen erst suchen, wo er hin ist.
      setzeAktivenBaum(ergebnis.treeId);
      queryClient.invalidateQueries({ queryKey: ['trees'] });
      queryClient.invalidateQueries({ queryKey: ['einladungen'] });
    },
  });

  const ablehnen = useMutation({
    mutationFn: () => lehneEinladungAb(token),
    onSuccess: () => {
      setErledigt('abgelehnt');
      queryClient.invalidateQueries({ queryKey: ['einladungen'] });
    },
  });

  if (!token) {
    return (
      <Rahmen>
        <OffeneListe />
      </Rahmen>
    );
  }

  if (isLoading) {
    return (
      <Rahmen>
        <div className="flex justify-center py-20">
          <LoadingSpinner />
        </div>
      </Rahmen>
    );
  }

  if (erledigt === 'angenommen') {
    return (
      <Rahmen>
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="text-lg font-medium text-slate-900">
            Sie gehören jetzt zu „{data?.baumName}".
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Der Stammbaum ist ab sofort in Ihrem Umschalter ausgewählt.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Zum Stammbaum
          </button>
        </div>
      </Rahmen>
    );
  }

  if (erledigt === 'abgelehnt') {
    return (
      <Rahmen>
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="text-lg font-medium text-slate-900">Abgelehnt.</p>
          <p className="mt-2 text-sm text-slate-500">
            Sie wurden nicht aufgenommen, und der Stammbaum bleibt Ihnen
            verborgen. Wer Sie eingeladen hat, kann es erneut versuchen.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Zurück
          </Link>
        </div>
      </Rahmen>
    );
  }

  if (isError || !data) {
    return (
      <Rahmen>
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-medium text-slate-900">
            Diese Einladung gibt es nicht mehr
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Möglich ist beides: Sie wurde zurückgezogen, oder die Frist ist
            abgelaufen. Möglich ist außerdem, dass der Link an eine andere
            Adresse gerichtet war als das Konto, mit dem Sie gerade angemeldet
            sind. Wer Sie eingeladen hat, kann es erneut versuchen.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Zurück
          </Link>
        </div>
      </Rahmen>
    );
  }

  const laeuft = annehmen.isPending || ablehnen.isPending;

  return (
    <Rahmen>
      <h1 className="text-xl font-semibold text-slate-900">Einladung zu einem Stammbaum</h1>

      <div className="mt-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-slate-700">
          {data.einladerEmail ? (
            <>
              <strong>{data.einladerEmail}</strong> möchte Sie zum Stammbaum{' '}
            </>
          ) : (
            <>Sie wurden zum Stammbaum </>
          )}
          <strong>{data.baumName}</strong> hinzufügen.
        </p>

        <dl className="mt-4 space-y-1 rounded-md bg-slate-50 px-3 py-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Vorgesehene Rolle</dt>
            <dd className="text-slate-800">{data.rolleName}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Gültig bis</dt>
            <dd className="text-slate-800">
              <Datum wert={data.gueltigBis} />
            </dd>
          </div>
        </dl>

        <p className="mt-4 text-sm leading-relaxed text-slate-500">
          Nehmen Sie an, sehen Sie die Personen dieses Stammbaums im Rahmen
          Ihrer Rolle – das sind Daten über lebende Angehörige. Lehnen Sie ab,
          passiert nichts: der Stammbaum bleibt Ihnen verborgen.
        </p>

        {(annehmen.isError || ablehnen.isError) && (
          <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
            Das hat nicht geklappt. Möglicherweise wurde die Einladung
            inzwischen zurückgezogen.
          </p>
        )}

        {/* Zustimmen und Ablehnen gleich groß. Eine Ablehnung, die man suchen
            muss, ist keine echte Wahl. */}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <button
            onClick={() => annehmen.mutate()}
            disabled={laeuft}
            className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {annehmen.isPending ? 'Einen Moment…' : 'Annehmen'}
          </button>
          <button
            onClick={() => ablehnen.mutate()}
            disabled={laeuft}
            autoFocus={gleichAblehnen}
            className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {ablehnen.isPending ? 'Einen Moment…' : 'Ablehnen'}
          </button>
        </div>
      </div>
    </Rahmen>
  );
}
